/**
 * Pure geometry + flow-field math for the click-to-inspect probe — no DOM, no module state, so
 * it's unit-testable. preview.ts's canvas click handler wires these to the current selection:
 * click ON an element (pointInPoly) → its Cp/Cf; click in the flow → a wall-normal local-Mach
 * profile (profilePoints) to the nearest smooth surface point (nearestSurfacePoint).
 */

/**
 * Pure flow-mesh data the probe reads. Inlined from the original `./lic` module (which depends on
 * three.js for rendering) so this geometry package stays free of three/DOM imports.
 */
export interface FlowMesh {
  points: number[];          // flat [x0,y0,x1,y1,…] (UI coords)
  tris: number[];            // flat [i0,j0,k0,…]
  vel: number[];             // flat [u0,v0,…]
  mach: number[];
  machRange: [number, number];
}

/** Is world point (x,z) inside the closed polygon (ray casting)? */
export function pointInPoly(x: number, z: number, poly: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const pi = poly[i], pj = poly[j];
    if (!pi || !pj) continue;
    const xi = pi[0]!, zi = pi[1]!, xj = pj[0]!, zj = pj[1]!;
    if ((zi > z) !== (zj > z) && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside;
}

/** Signed winding (+1 CCW / −1 CW) so a segment's OUTWARD normal can be oriented: for edge
 *  direction (dx,dz) the outward normal is sign·(dz,−dx). */
function windingSign(poly: number[][]): number {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const cur = poly[i], b = poly[(i + 1) % poly.length];
    if (!cur || !b) continue;
    a += cur[0]! * b[1]! - b[0]! * cur[1]!;
  }
  return a >= 0 ? 1 : -1;
}

/** Nearest wall point to (x,z) across the given element polygons, preferring a true wall-NORMAL
 *  foot (perpendicular projection landing WITHIN a segment) whose OUTWARD side faces the click.
 *  The normal test means a click above the vane can't snap to the vane's underside, and sharp
 *  tips/corners (cove-lip cut, TE cusp) — only ever segment endpoints — are never chosen when the
 *  click is perpendicular over a real surface. Falls back to the nearest endpoint only when there
 *  is no such foot (a click off the end of everything). */
export function nearestSurfacePoint(x: number, z: number, polys: number[][][]): [number, number] {
  let foot: [number, number] | null = null, fd = Infinity;   // best outward-facing perpendicular foot
  let end: [number, number] = [x, z], ed = Infinity;          // best endpoint fallback
  for (const poly of polys) {
    const s = windingSign(poly);
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i], b = poly[(i + 1) % poly.length];
      if (!a || !b) continue;
      const ax = a[0]!, az = a[1]!, bx = b[0]!, bz = b[1]!;
      const dx = bx - ax, dz = bz - az, len2 = dx * dx + dz * dz;
      if (len2 < 1e-12) continue;
      const t = ((x - ax) * dx + (z - az) * dz) / len2;
      const tc = Math.max(0, Math.min(1, t));
      const cx = ax + tc * dx, cz = az + tc * dz, d = (cx - x) ** 2 + (cz - z) ** 2;
      const outward = (x - cx) * (s * dz) + (z - cz) * (-s * dx) > 0;   // click on the wall's flow side
      if (t > 0 && t < 1 && outward) { if (d < fd) { fd = d; foot = [cx, cz]; } }
      else if (d < ed) { ed = d; end = [cx, cz]; }
    }
  }
  return foot ?? end;
}

/** Local-Mach profile along the segment a→b, interpolated ALONG the mesh edges it crosses (the
 *  flow field is piecewise-linear on the triangulation, so its restriction to the line is
 *  piecewise-linear with breakpoints exactly at edge crossings). This captures every cell-layer
 *  the line passes through — so the thin, anisotropic near-wall layers are each sampled correctly,
 *  instead of relying on point queries that alias or snap to a neighbouring cell. Uses the
 *  per-vertex LOCAL Mach (Flow360's `Mach` field = local speed / local sound speed), not |vel|
 *  (which is nondimensionalized by the FREESTREAM sound speed). Returns {d, m} sorted by distance
 *  along the line; the wall is no-slip (M = 0). */
export function profilePoints(flow: FlowMesh, a: number[], b: number[]): { d: number[]; m: number[] } {
  const P = flow.points, T = flow.tris, M = flow.mach;
  const ax = a[0] ?? 0, ay = a[1] ?? 0;
  const dx = (b[0] ?? 0) - ax, dy = (b[1] ?? 0) - ay, segLen = Math.hypot(dx, dy);
  const seen = new Set<number>();
  const hits: [number, number][] = [[0, 0]];         // (distance, local Mach); wall is no-slip (0)
  for (let t = 0; t < T.length; t += 3) {
    const p = T[t], q = T[t + 1], rIdx = T[t + 2];
    if (p === undefined || q === undefined || rIdx === undefined) continue;
    const tri = [p, q, rIdx];
    for (let e = 0; e < 3; e++) {
      const pe = tri[e]!, qe = tri[(e + 1) % 3]!;
      const key = pe < qe ? pe * 1e7 + qe : qe * 1e7 + pe;  // each undirected edge once
      if (seen.has(key)) continue;
      seen.add(key);
      const px = P[pe * 2] ?? 0, py = P[pe * 2 + 1] ?? 0;
      const ex = (P[qe * 2] ?? 0) - px, ey = (P[qe * 2 + 1] ?? 0) - py;
      const det = ex * dy - dx * ey;
      if (det === 0) continue;
      const wx = px - ax, wy = py - ay;
      const s = (ex * wy - ey * wx) / det;            // param along a→b
      const r = (dx * wy - dy * wx) / det;            // param along the edge p→q
      if (s >= 0 && s <= 1 && r >= 0 && r <= 1) {
        const mp = M[pe] ?? 0, mq = M[qe] ?? 0;
        hits.push([s * segLen, mp + r * (mq - mp)]);
      }
    }
  }
  hits.sort((m, n) => (m[0] ?? 0) - (n[0] ?? 0));
  return { d: hits.map((h) => h[0]), m: hits.map((h) => h[1]) };
}
